import React, { useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Button, Collapse, Fab, Stack, Typography } from '@mui/material'
import { com } from '@eidu/form'
import { Add } from '@mui/icons-material'
import exportEntitiesOfType from '../../io/api/entity/data/exportEntitiesOfType'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import TanStackDataGrid from '../../components/data/TanStackDataGrid'
import useEntityRepository from '../../io/useEntityRepository'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import EntityTypeNotAccessibleError from './EntityTypeNotAccessibleError'
import EntityRepository from '../../io/EntityRepository'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString

type ListPageContentProps = {
  onImport: () => void
  onNew: () => void
  onDownloadCsv: () => void
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  entityRepository: EntityRepository
  pageSize: number
}

export const ListPageContent = ({
  onImport,
  onNew,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDownloadCsv,
  entityType,
  entityTypes,
  entityRepository,
  pageSize,
}: ListPageContentProps) => {
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        overflow: 'hidden',
      }}
    >
      <Fab
        data-testid="entity-list-create-button"
        color="primary"
        aria-label={`Create ${entityType.name}`}
        onClick={onNew}
        sx={{
          position: 'fixed',
          bottom: (theme) => theme.spacing(2),
          right: (theme) => theme.spacing(2),
        }}
      >
        <Add />
      </Fab>
      {entityType && (
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ flexShrink: 0 }}>
          <Typography variant="h4" data-testid="entity-list-title">
            {entityType.name}
          </Typography>
          <Stack direction="row" justifyContent="flex-end">
            <Box>
              <Button data-testid="entity-list-import-button" color="primary" onClick={onImport}>
                Import
              </Button>
            </Box>
          </Stack>
        </Stack>
      )}
      {entityType && (
        <Box sx={{ flex: 1, overflow: 'hidden', pt: 1 }}>
          <TanStackDataGrid
            navigate={navigate}
            entityRepository={entityRepository}
            entityTypes={entityTypes}
            entityType={entityType}
            pageSize={pageSize}
          />
        </Box>
      )}
    </Box>
  )
}

export type ReadyListPageProps = {
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyListPage = ({ entityType, entityTypes }: ReadyListPageProps) => {
  const navigate = useNavigate()
  const [errorPreparingExport, setErrorPreparingExport] = useState<string | undefined>(undefined)

  const entityRepository = useEntityRepository(
    {
      typeId: entityType.id,
      types: entityTypes,
    },
    [entityType.id.toString(), entityTypes.size]
  )

  const prepareCsvDownload = () => {
    exportEntitiesOfType({ id: entityType.id })
      .then((jobId) => {
        const args = new URLSearchParams({ jobId }).toString()
        navigate(`/export?${args}`)
      })
      .catch((err) => {
        setErrorPreparingExport(`Failed to prepare export: ${err}`)
      })
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Collapse in={!!errorPreparingExport}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <Box>{errorPreparingExport}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      <ListPageContent
        onImport={() => {
          navigate('import')
        }}
        onNew={() => {
          navigate('new')
        }}
        onDownloadCsv={prepareCsvDownload}
        entityType={entityType}
        entityTypes={entityTypes}
        entityRepository={entityRepository}
        pageSize={50}
      />
      <LoadingOverlay isOpen={!entityType} />
    </Stack>
  )
}

export type ListPageProps = {
  entityTypeId: EntityTypeId
  entityTypeRepository: EntityTypeRepository
}

const ListPage = ({ entityTypeId, entityTypeRepository }: ListPageProps) => {
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const error = entityTypeRepository.useError()?.let((it) => String(it))

  const loadingType = entityTypeRepository.useLoading()
  const loadingOverlayOpen = loadingType && !error

  return (
    <>
      {entityType && entityTypes && (
        <ReadyListPage key={entityType.id.toString()} entityType={entityType} entityTypes={entityTypes} />
      )}
      {(!entityType || !entityTypes) && !loadingOverlayOpen && <EntityTypeNotAccessibleError />}
    </>
  )
}

const ListPageRoute = () => {
  const params = useParams()
  const entityTypeRepository = useContext(EntityTypesContext)

  if (params.entityTypeId)
    return (
      <ListPage
        entityTypeId={entityTypeIdFromString(params.entityTypeId)}
        entityTypeRepository={entityTypeRepository}
      />
    )
  else return <Typography sx={{ padding: 3 }}>Unknown state</Typography>
}

export default ListPageRoute
